import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Paper,
  FormHelperText,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  Business,
  LocationOn,
  Language,
  Note,
  Add,
  Delete,
  LinkedIn,
  Twitter,
  Facebook,
  Public,
  Check,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import {
  Section,
  contactFormRootSx,
  itemPaperSx,
  primaryActionButtonSx,
  removeItemButtonSx,
  footerSx,
} from "./contactFormStyles";

const schema = yup
  .object()
  .shape({
    first_name: yup.string().required("First name is required"),
    last_name: yup.string(),
    emails: yup.array().of(
      yup.object().shape({
        email: yup.string().email("Invalid email"),
        type: yup.string().required("Email type is required"),
      })
    ),
    phones: yup
      .array()
      .of(
        yup.object().shape({
          number: yup.string(),
          type: yup.string().required("Phone type is required"),
        })
      )
      .test(
        "phone-or-email-required",
        "At least one phone number or email address is required",
        function (phones) {
          const emails = this.parent.emails;
          const hasPhone =
            Array.isArray(phones) &&
            phones.some((p) => p?.number && p.number.trim() !== "");
          const hasEmail =
            Array.isArray(emails) &&
            emails.some((e) => e?.email && e.email.trim() !== "");
          return hasPhone || hasEmail;
        }
      ),
    position: yup.string(),
    industry_type: yup.string(),
    employee_count: yup
      .number()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .typeError("Employee count must be a number"),
    source: yup
      .number()
      .required("Source is required")
      .typeError("Source is required"),
    status: yup
      .number()
      .required("Status is required")
      .typeError("Status is required"),
    notes: yup.string(),
    social_media: yup.object().shape({
      linkedin: yup.string().url("Must be a valid URL"),
      twitter: yup.string(),
      facebook: yup.string().url("Must be a valid URL"),
      website: yup.string().url("Must be a valid URL"),
    }),
    assigned_to: yup.string().required("Assigned to is required"),
  });

const emailTypeOptions = [
  { value: "work", label: "Work" },
  { value: "personal", label: "Personal" },
  { value: "other", label: "Other" },
];

const phoneTypeOptions = [
  { value: "mobile", label: "Mobile" },
  { value: "work", label: "Work" },
  { value: "home", label: "Home" },
  { value: "other", label: "Other" },
];

const EMPTY_ADDRESS = {
  street: "",
  city: "",
  state: "",
  country: "",
  postal_code: "",
  type: "work",
};

export default function EditContactPage({
  contactId,
  drawerMode = false,
  hideHeader = false,
  onClose,
  onSaved,
} = {}) {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const params = useParams();
  const id = contactId ?? params.id;
  const history = useHistory();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [empData, setEmpData] = useState(null);
  const [showAutoCompleteAvatar, setShowAutoCompleteAvatar] = useState(false);
  const { result: users } = useFetch(
    `${domain}crm/crm-members/active/?company_id=${requestCompanyId}`
  );
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const formValues = watch();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      history.push("/admin/crm/contacts");
    }
  };

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-status/?page_size=100`);
        setStatusOptions(res.responseDetails?.results || []);
      } catch (err) {}
    };

    const fetchSourceOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-source/?page_size=100`);
        setSourceOptions(res.responseDetails?.results || []);
      } catch (err) {}
    };

    fetchStatusOptions();
    fetchSourceOptions();
  }, [domain]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/companies/autocomplete/`);
        setCompanyOptions(res.responseDetails ?? []);
      } catch {}
    };
    fetchCompanies();
  }, [domain]);

  useEffect(() => {
    const loadContact = async () => {
      if (!users || users.length === 0) return;
      try {
        const response = await customGetAPI(`${domain}crm/contacts/${id}/`);
        const contact = response.responseDetails;
        const assignedUser = users.find(
          (user) => user.id === contact.assigned_to
        );
        setEmpData(assignedUser || null);

        // Pre-fill company autocomplete
        if (contact.company_fk && contact.company) {
          setSelectedCompany({ id: contact.company_fk, name: contact.company });
        }

        const formData = {
          first_name: contact.first_name,
          last_name: contact.last_name,
          position: contact.position,
          industry_type: contact.industry_type || "",
          employee_count: contact.employee_count ?? "",
          assigned_to: contact.assigned_to,

          source: contact.source,
          status: contact.status,
          notes: contact.notes,
          social_media: {
            linkedin: contact.linkedin || "",
            twitter: contact.twitter || "",
            facebook: contact.facebook || "",
            website: contact.website || "",
          },
          emails: contact.emails || [
            { email: "", type: "work", primary: true },
          ],
          phones: contact.phone_numbers?.map((phone) => ({
            number: phone.number,
            type: phone.type || 'mobile',
            primary: true,
          })) || [{ number: "", type: "mobile", primary: true }],
        };

        reset(formData);
        const existingAddr = contact?.addresses?.[0];
        setAddress(existingAddr ? {
          street: existingAddr.street || "",
          city: existingAddr.city || "",
          state: existingAddr.state || "",
          country: existingAddr.country || "",
          postal_code: existingAddr.postal_code || "",
          type: existingAddr.type || "work",
        } : EMPTY_ADDRESS);
        if (users?.length) {
          const assignedUser = users.find(
            (user) => user.id === contact.assigned_to
          );
          if (assignedUser) {
            setEmpData({
              ...assignedUser,
              label: assignedUser.username,
              image: assignedUser.image_url,
            });
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error('[Editcontactpage] operation failed:', error);
        toast.error("Failed to load contact");
        if (onClose) {
          onClose();
        } else {
          history.push("/admin/crm");
        }
      }
    };

    loadContact();
  }, [id, domain, reset, history, users, onClose]);

  // Helper functions (same as ContactFormPage)
  const addArrayItem = (arrayName, newItem) => {
    const currentArray = getValues(arrayName);
    setValue(arrayName, [...currentArray, newItem]);
  };

  const removeArrayItem = (arrayName, index) => {
    const currentArray = getValues(arrayName);
    setValue(
      arrayName,
      currentArray.filter((_, i) => i !== index)
    );
  };

  const validateFormData = (data) => {
    let isValid = true;

    // Check for duplicate emails
    if (data.emails) {
      const emailErrors = {};
      const emailValues = data.emails.map((e) => e.email?.trim().toLowerCase());

      data.emails.forEach((email, index) => {
        if (
          email.email &&
          emailValues.indexOf(email.email.trim().toLowerCase()) !== index
        ) {
          emailErrors[index] = { email: { message: "Duplicate email" } };
          isValid = false;
        }
      });

      if (Object.keys(emailErrors).length > 0) {
        Object.entries(emailErrors).forEach(([index, error]) => {
          setError(`emails.${index}`, error);
        });
      }
    }

    // Check for duplicate phone numbers
    if (data.phones) {
      const phoneErrors = {};
      const phoneValues = data.phones.map((p) => p.number?.trim());

      data.phones.forEach((phone, index) => {
        if (
          phone.number &&
          phoneValues.indexOf(phone.number.trim()) !== index
        ) {
          phoneErrors[index] = {
            number: { message: "Duplicate phone number" },
          };
          isValid = false;
        }
      });

      if (Object.keys(phoneErrors).length > 0) {
        Object.entries(phoneErrors).forEach(([index, error]) => {
          setError(`phones.${index}`, error);
        });
      }
    }

    return isValid;
  };

  const resolveCompanyFk = async () => {
    if (!selectedCompany) return null;
    if (typeof selectedCompany === "object" && selectedCompany.id) return selectedCompany.id;
    const name = typeof selectedCompany === "string" ? selectedCompany : selectedCompany.name;
    if (!name?.trim()) return null;
    try {
      const res = await customAPI(`${domain}crm/companies/`, "POST", { name: name.trim() });
      return res.responseDetails?.id ?? null;
    } catch {
      toast.error("Failed to create account");
      return null;
    }
  };

  const handleAddNewCompany = async (name) => {
    if (!name?.trim()) return;
    try {
      const res = await customAPI(`${domain}crm/companies/`, "POST", { name: name.trim() });
      const newCompany = res.responseDetails;
      if (newCompany?.id) {
        const companyObj = { id: newCompany.id, name: newCompany.name || name.trim() };
        setSelectedCompany(companyObj);
        try {
          const optRes = await customGetAPI(`${domain}crm/companies/autocomplete/`);
          setCompanyOptions(optRes.responseDetails ?? []);
        } catch {}
      }
    } catch {
      toast.error("Failed to create account");
    }
  };

  const preparePayload = (data, resolvedCompanyFkId) => {
    return {
      first_name: data.first_name,
      last_name: data.last_name,
      company_fk: resolvedCompanyFkId ?? null,
      position: data.position,
      industry_type: data.industry_type || "",
      employee_count: data.employee_count || null,
      assigned_to: data.assigned_to,
      facebook: data.social_media.facebook,
      twitter: data.social_media.twitter,
      linkedin: data.social_media.linkedin,
      phone_numbers: data.phones
        .filter((phone) => phone.number && phone.number.trim() !== "")
        .map((phone) => ({
          number: phone.number,
          type: phone.type || 'mobile',
        })),
      emails: data.emails.map((email) => ({
        email: email.email,
        type: email.type,
      })),
      addresses: Object.values({
        street: address.street,
        city: address.city,
        state: address.state,
        country: address.country,
        postal_code: address.postal_code,
      }).some(Boolean)
        ? [address]
        : [],
      source: data.source,
      status: data.status,
      notes: data.notes,
      website: data.social_media.website,
    };
  };

  const onSubmit = async (data) => {
    if (!validateFormData(data)) {
      return;
    }

    setIsSaving(true);

    const companyFkId = await resolveCompanyFk();
    const payload = preparePayload(data, companyFkId);

    try {
      const response = await customAPI(`${domain}crm/contacts/${id}/`, "PUT", payload);
      // customAPI has already shown the server's reason; stay on the form.
      if (!response?.ok) return;
      const updatedContact = response?.data || response?.responseDetails || response;
      toast.success("Contact updated.");
      if (onSaved) {
        await onSaved({ contact: updatedContact });
      } else {
        history.push("/admin/crm/contacts");
      }
    } catch (error) {
      console.error('[Editcontactpage] operation failed:', error);
      toast.error(error.response?.data?.message || "Failed to update contact");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={drawerMode ? "60vh" : "80vh"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={contactFormRootSx(drawerMode)}>
      {!hideHeader && (
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={handleClose} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            Edit Contact
          </Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Section title="Contact Information" icon={Person}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name *"
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={companyOptions}
                getOptionLabel={(o) => {
                  if (typeof o === "string") return o;
                  if (o.isAddNew) return o.typedName;
                  return o.name;
                }}
                value={selectedCompany}
                onChange={(_, val) => {
                  if (val?.isAddNew) {
                    handleAddNewCompany(val.typedName);
                  } else {
                    setSelectedCompany(val);
                  }
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === "input") setSelectedCompany(val || null);
                }}
                isOptionEqualToValue={(option, value) => {
                  if (option.isAddNew) return false;
                  if (typeof value === "string") return option.name === value;
                  return option.id === value?.id;
                }}
                filterOptions={(options, { inputValue: iv }) => {
                  const q = iv.toLowerCase();
                  const filtered = options.filter((o) =>
                    o.name.toLowerCase().includes(q)
                  );
                  const exactMatch = filtered.some(
                    (o) => o.name.toLowerCase() === iv.trim().toLowerCase()
                  );
                  if (iv.trim() !== "" && !exactMatch) {
                    filtered.push({
                      id: "__add_new__",
                      name: iv,
                      isAddNew: true,
                      typedName: iv,
                    });
                  }
                  return filtered;
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.isAddNew ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: "#1D9E75", fontWeight: 600 }}
                      >
                        <Add fontSize="small" />
                        <span>+ Add new account: {option.typedName}</span>
                      </Box>
                    ) : (
                      option.name
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account"
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Search or type an account name"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Business color="primary" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="position"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Position"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="industry_type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Sector"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="employee_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Employee Count"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!errors.employee_count}
                    helperText={errors.employee_count?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Contact Details" icon={Email}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>
            Add at least one phone number (required)
          </Typography>

          {formValues.emails?.map((email, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={itemPaperSx}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <Controller
                    name={`emails.${index}.email`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={!!errors.emails?.[index]?.email}
                        helperText={
                          errors.emails?.[index]?.email?.message ||
                          (errors.emails?.[index]?.email?.type ===
                            "duplicate" &&
                            "Duplicate email")
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="primary" />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={10} sm={4}>
                  <Controller
                    name={`emails.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Type *</InputLabel>
                        <Select
                          {...field}
                          label="Type *"
                          variant="outlined"
                          size="small"
                          error={!!errors.emails?.[index]?.type}
                          InputProps={{
                            sx: { borderRadius: 2 },
                          }}
                        >
                          {emailTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={2} sm={2}>
                  <Box display="flex" justifyContent="flex-end">
                    {formValues.emails.length > 1 && (
                      <Tooltip title="Remove email">
                        <IconButton
                          onClick={() => removeArrayItem("emails", index)}
                          color="error"
                          sx={removeItemButtonSx}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              addArrayItem("emails", {
                email: "",
                type: "work",
                primary: false,
              })
            }
            sx={{ mb: 4 }}
          >
            Add Email
          </Button>

          <Divider sx={{ my: 3 }} />

          {formValues.phones?.map((phone, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={itemPaperSx}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`phones.${index}.number`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number *"
                        fullWidth
                        size="small"
                        variant="outlined"
                        error={!!errors.phones?.[index]?.number}
                        helperText={
                          errors.phones?.[index]?.number?.message ||
                          (errors.phones?.[index]?.number?.type ===
                            "duplicate" &&
                            "Duplicate phone number")
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="primary" />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`phones.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Type *</InputLabel>
                        <Select
                          {...field}
                          label="Type *"
                          variant="outlined"
                          size="small"
                          error={!!errors.phones?.[index]?.type}
                        >
                          {phoneTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={4} sm={2} md={1}>
                  <Box display="flex" justifyContent="flex-end">
                    {formValues.phones.length > 1 && (
                      <Tooltip title="Remove phone">
                        <IconButton
                          onClick={() => removeArrayItem("phones", index)}
                          color="error"
                          sx={removeItemButtonSx}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              addArrayItem("phones", {
                number: "",
                type: "mobile",
                primary: false,
              })
            }
          >
            Add Phone
          </Button>
          {errors.phones?.message && (
            <FormHelperText error sx={{ mt: 2 }}>
              {errors.phones.message}
            </FormHelperText>
          )}
          <FormHelperText sx={{ mt: 1, ml: 0.5 }}>
            Include country code for CRM calling (e.g. +91 9876543210 or +44 7700 900077)
          </FormHelperText>
        </Section>

        <Section title="CRM Intelligence" icon={Business}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="source"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Source *</InputLabel>
                    <Select
                      {...field}
                      label="Source *"
                      variant="outlined"
                      size="small"
                      error={!!errors.source}
                    >
                      {sourceOptions.map((option) => (
                        <MenuItem key={option.value} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.source && (
                      <FormHelperText error>
                        {errors.source.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status *</InputLabel>
                    <Select
                      {...field}
                      label="Status *"
                      variant="outlined"
                      size="small"
                      error={!!errors.status}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.status && (
                      <FormHelperText error>
                        {errors.status.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((user) => ({
                  ...user,
                  label: user.username, // Only add label if absolutely required
                  image: user.image_url,
                }))}
                selectedValue={empData}
                updateDataFunction={(e, newValue) => {
                  setEmpData(newValue);
                  setValue("assigned_to", newValue?.id || ""); // Store ID
                }}
                placeHolder="Select user name *"
                showAvathar={showAutoCompleteAvatar}
                getOptionLabel={(option) => option?.username} // Display username
                getOptionImage={(option) => option?.image_url}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvatar}
                error={!!errors.assigned_to}
              />
              {errors.assigned_to && (
                <FormHelperText error sx={{ mt: -1, ml: 2 }}>
                  {errors.assigned_to.message}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Section>

        <Section title="Addresses" icon={LocationOn}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street"
                multiline
                rows={2}
                placeholder="Enter street address"
                value={address.street}
                onChange={(e) => setAddress((prev) => ({ ...prev, street: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                placeholder="Enter city"
                value={address.city}
                onChange={(e) => setAddress((prev) => ({ ...prev, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={address.state}
                onChange={(e) => setAddress((prev) => ({ ...prev, state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={address.postal_code}
                onChange={(e) => setAddress((prev) => ({ ...prev, postal_code: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={address.country}
                onChange={(e) => setAddress((prev) => ({ ...prev, country: e.target.value }))}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Social Media & Website" icon={Language}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="social_media.website"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Website"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="https://example.com"
                    error={!!errors.social_media?.website}
                    helperText={errors.social_media?.website?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Public color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="social_media.linkedin"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="LinkedIn Profile URL"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="https://linkedin.com/in/..."
                    error={!!errors.social_media?.linkedin}
                    helperText={errors.social_media?.linkedin?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedIn color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="social_media.twitter"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Twitter Handle"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="@username"
                    error={!!errors.social_media?.twitter}
                    helperText={errors.social_media?.twitter?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Twitter color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="social_media.facebook"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Facebook Profile URL"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="https://facebook.com/..."
                    error={!!errors.social_media?.facebook}
                    helperText={errors.social_media?.facebook?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Facebook color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Additional Details" icon={Note}>
          <Box mb={4}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              )}
            />
          </Box>
        </Section>

        <Box
          sx={footerSx(drawerMode)}
        >
          <Box
            display="flex"
            justifyContent="flex-end"
            maxWidth={drawerMode ? "none" : 2000}
            mx="auto"
          >
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={handleClose}
                //to="/admin/crm/contacts"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <Check />
                }
                sx={{
                  ...primaryActionButtonSx,
                }}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
}
